import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrainTrainingGameIcon, GameShell, shuffle } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const BALLS = ['blue', 'pink', 'yellow', 'green'];
const PEG_CAPACITY = 3;
const { normalizeBrainGameLevel } = brainGameLevels;

const cloneState = (state) => state.map((peg) => [...peg]);
const stateKey = (state) => state.map((peg) => peg.join(',')).join('|');

const availableMoves = (state) => {
    const moves = [];
    state.forEach((source, sourceIndex) => {
        if (!source.length) {
            return;
        }
        state.forEach((target, targetIndex) => {
            if (sourceIndex !== targetIndex && target.length < PEG_CAPACITY) {
                moves.push([sourceIndex, targetIndex]);
            }
        });
    });
    return moves;
};

const applyMove = (state, sourceIndex, targetIndex) => {
    const next = cloneState(state);
    next[targetIndex].push(next[sourceIndex].pop());
    return next;
};

const shortestDistance = (start, goal) => {
    const goalKey = stateKey(goal);
    const seen = new Set([stateKey(start)]);
    const queue = [{ depth: 0, state: start }];

    while (queue.length) {
        const current = queue.shift();
        if (stateKey(current.state) === goalKey) {
            return current.depth;
        }
        availableMoves(current.state).forEach(([source, target]) => {
            const next = applyMove(current.state, source, target);
            const key = stateKey(next);
            if (!seen.has(key)) {
                seen.add(key);
                queue.push({ depth: current.depth + 1, state: next });
            }
        });
    }

    return 0;
};

const createTowerRound = (level = 1, trialIndex = 1) => {
    const start = trialIndex % 2 === 0
        ? [[BALLS[0], BALLS[1]], [BALLS[2]], [BALLS[3]]]
        : [[BALLS[0]], [BALLS[1], BALLS[2]], [BALLS[3]]];
    let goal = cloneState(start);
    let previousMove = null;
    const scrambleMoves = 3 + level;

    for (let index = 0; index < scrambleMoves; index += 1) {
        const candidates = availableMoves(goal).filter(([source, target]) => (
            !previousMove || source !== previousMove[1] || target !== previousMove[0]
        ));
        const move = shuffle(candidates)[0] || shuffle(availableMoves(goal))[0];
        goal = applyMove(goal, move[0], move[1]);
        previousMove = move;
    }

    if (stateKey(goal) === stateKey(start)) {
        goal = applyMove(goal, 0, 2);
    }

    return {
        goal,
        id: `${trialIndex}-${level}-${Date.now()}`,
        optimalMoves: shortestDistance(start, goal),
        start,
    };
};

function TowerBoard({ copy = {}, state, interactive = false, selectedPeg = null, onPegClick = null }) {
    return (
        <div className={`UFOV-brain-games__tower-board ${interactive ? 'is-interactive' : 'is-goal'}`}>
            {state.map((peg, pegIndex) => (
                <button
                    aria-label={formatGameCopy(copy.peg, { number: pegIndex + 1 })}
                    className={selectedPeg === pegIndex ? 'is-selected' : ''}
                    disabled={!interactive}
                    key={pegIndex}
                    onClick={() => onPegClick?.(pegIndex)}
                    type="button"
                >
                    <span className="UFOV-brain-games__tower-pole" aria-hidden="true" />
                    <span className="UFOV-brain-games__tower-balls">
                        {[...peg].reverse().map((ball) => (
                            <i className={`is-${ball}`} key={ball} />
                        ))}
                    </span>
                </button>
            ))}
        </div>
    );
}

function TowerPlanGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('tower-plan', initialLevel);
    const timerRef = useRef(null);
    const resolvingRoundRef = useRef(false);
    const [roundIndex, setRoundIndex] = useState(1);
    const [level, setLevel] = useState(startingLevel);
    const [round, setRound] = useState(() => createTowerRound(startingLevel, 1));
    const [state, setState] = useState(() => cloneState(round.start));
    const [selectedPeg, setSelectedPeg] = useState(null);
    const [moves, setMoves] = useState(0);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [mistakes, setMistakes] = useState(0);
    const [tone, setTone] = useState('ready');
    const solved = useMemo(() => stateKey(state) === stateKey(round.goal), [round.goal, state]);

    useEffect(() => () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
        }
    }, []);

    useEffect(() => {
        if (!solved || moves === 0 || resolvingRoundRef.current) {
            return undefined;
        }

        resolvingRoundRef.current = true;
        const optimal = moves <= round.optimalMoves;
        const nextScore = score + 1;
        const nextLevel = normalizeBrainGameLevel(
            'tower-plan',
            startingLevel + Math.floor(nextScore / 3)
        );
        setScore(nextScore);
        setStreak((current) => (optimal ? current + 1 : 0));
        setTone('good');
        timerRef.current = window.setTimeout(() => {
            const nextRoundIndex = roundIndex + 1;
            const nextRound = createTowerRound(nextLevel, nextRoundIndex);
            setRoundIndex(nextRoundIndex);
            setLevel(nextLevel);
            setRound(nextRound);
            setState(cloneState(nextRound.start));
            setSelectedPeg(null);
            setMoves(0);
            setTone('ready');
            resolvingRoundRef.current = false;
        }, 1200);

        return undefined;
    }, [moves, round, roundIndex, score, solved, startingLevel]);

    const choosePeg = (pegIndex) => {
        if (solved) {
            return;
        }

        if (selectedPeg === null) {
            if (state[pegIndex].length) {
                setSelectedPeg(pegIndex);
            }
            return;
        }

        if (selectedPeg === pegIndex) {
            setSelectedPeg(null);
            return;
        }

        if (state[pegIndex].length >= PEG_CAPACITY) {
            setMistakes((current) => current + 1);
            setTone('miss');
            setSelectedPeg(null);
            return;
        }

        setState((current) => applyMove(current, selectedPeg, pegIndex));
        setMoves((current) => current + 1);
        setSelectedPeg(null);
        setTone('ready');
    };

    return (
        <GameShell {...props} copy={copy} metrics={{ level, mistakes, score, streak }} tone={tone}>
            <div className={`UFOV-brain-games__tower-game ${solved ? 'is-solved' : ''}`.trim()}>
                <div className="UFOV-brain-games__tower-copy-card">
                    <span className="UFOV-brain-games__tower-copy-label">{copy.towerCopyCue}</span>
                    <TowerBoard copy={copy} state={round.goal} />
                </div>
                <div className="UFOV-brain-games__tower-divider" aria-hidden="true" />
                <TowerBoard copy={copy} interactive onPegClick={choosePeg} selectedPeg={selectedPeg} state={state} />
                {solved ? (
                    <span
                        aria-label={copy.planComplete}
                        className="UFOV-brain-games__tower-complete"
                        role="status"
                    >
                        <BrainTrainingGameIcon icon="check" />
                    </span>
                ) : null}
            </div>
        </GameShell>
    );
}

export default TowerPlanGame;
